import React from 'react'

import {
    CircularProgress,
    Modal,
    Paper,
    Slide,
    useTheme,
    Button,
    Chip,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'

import { User } from '../../../models'
import { OrganizationUserTable } from './OrganizationUserTable'
import { SearchField } from '../../../components/SearchField'
import { Container } from '../../../components'
import { useOrganizationManagement } from '../../../hooks/useOrganizationManagement'
import { RemoveUserWorkspaces } from './RemoveUserWorkspaces'
import { RootState } from '../../../store'
import { AddUserToWorkspaces } from './AddUserToWorkspaces'
import { UserActivitySummary } from './UserActivitySummary'
import { AddNewUserToWorkspaces } from './AddNewUserToWorkspaces'
import { useUser } from '../../../hooks'

interface OrganizationUsersRootProps {
    loadingState: ReturnType<typeof useOrganizationManagement>['loadingState']
    filteredUserList: ReturnType<typeof useOrganizationManagement>['userList']
    userListActivityMap: ReturnType<
        typeof useOrganizationManagement
    >['userListActivityMap']
    userDetail: ReturnType<typeof useOrganizationManagement>['userDetail']
    userDetailActivityMap: ReturnType<
        typeof useOrganizationManagement
    >['userDetailActivityMap']
    organization: ReturnType<typeof useUser>['organization']
    permissionRoleList: RootState['permission']['permissionRoleList']
    companyList: ReturnType<typeof useOrganizationManagement>['companyList']
    searchQuery: string
    showRegionalManagersOnly: boolean
    showPropertyManagersOnly: boolean
    modalState: UserPageModalState
    setSearchQuery: (query: string) => void
    onToggleRegionalManagersOnly: () => void
    onTogglePropertyManagersOnly: () => void
    onClickAddNewUser: () => void
    onClickRemoveUserFromWorkspaces: (user: User) => void
    onClickAddUser: (user: User) => void
    onClickUserActivity: (user: User) => void
    onClickMarkAsRegionalManager: (user: User) => void
    onClickMarkAsPropertyManager: (user: User, companyId: number) => void
    handleCloseRemoveUserWorkspaces: () => void
    handleCloseAddUser: () => void
    handleCloseUserActivity: () => void
    handleCloseAddNewUser: () => void
    bulkArchiveUser: ReturnType<
        typeof useOrganizationManagement
    >['bulkArchiveUser']
    setUserList: ReturnType<typeof useOrganizationManagement>['setUserList']
    bulkAddUserToWorkspaces: ReturnType<
        typeof useOrganizationManagement
    >['bulkAddUserToWorkspaces']
}

const OrganizationUsersRoot = (props: OrganizationUsersRootProps) => {
    const {
        loadingState,
        companyList,
        userDetail,
        userListActivityMap,
        userDetailActivityMap,
        bulkArchiveUser,
        setUserList,
        bulkAddUserToWorkspaces,
        filteredUserList,
        organization,
        permissionRoleList,
        searchQuery,
        showRegionalManagersOnly,
        showPropertyManagersOnly,
        modalState,
        setSearchQuery,
        onToggleRegionalManagersOnly,
        onTogglePropertyManagersOnly,
        onClickAddNewUser,
        onClickRemoveUserFromWorkspaces,
        onClickAddUser,
        onClickUserActivity,
        onClickMarkAsRegionalManager,
        onClickMarkAsPropertyManager,
        handleCloseRemoveUserWorkspaces,
        handleCloseAddUser,
        handleCloseUserActivity,
        handleCloseAddNewUser,
    } = props

    const theme = useTheme()

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                width: '100%',
                overflow: 'hidden',
                padding: 0,
                maxWidth: 'none',
            }}
        >
            <Container
                style={{
                    width: '100%',
                    padding: theme.spacing(1),
                    paddingBottom: 0,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: theme.spacing(1),
                    flexWrap: 'wrap',
                }}
            >
                <Container
                    style={{
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: theme.spacing(0.75),
                        flexWrap: 'wrap',
                        padding: 0,
                        flex: 1,
                    }}
                >
                    <SearchField
                        value={searchQuery}
                        style={{
                            maxWidth: 300,
                        }}
                        variant="outlined"
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        placeholder="Search"
                    />
                    <Container
                        style={{
                            alignItems: 'center',
                            gap: theme.spacing(0.75),
                            padding: 0,
                        }}
                    >
                        <Chip
                            label="Regional Managers"
                            clickable
                            color={
                                showRegionalManagersOnly ? 'primary' : 'default'
                            }
                            variant={
                                showRegionalManagersOnly
                                    ? 'default'
                                    : 'outlined'
                            }
                            onClick={onToggleRegionalManagersOnly}
                        />
                        <Chip
                            label="Property Managers"
                            clickable
                            color={
                                showPropertyManagersOnly ? 'primary' : 'default'
                            }
                            variant={
                                showPropertyManagersOnly
                                    ? 'default'
                                    : 'outlined'
                            }
                            onClick={onTogglePropertyManagersOnly}
                        />
                    </Container>
                </Container>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onClickAddNewUser}
                    style={{
                        textTransform: 'none',
                        backgroundColor: theme.palette.primary.dark,
                        color: theme.palette.primary.contrastText,
                    }}
                >
                    Add User
                </Button>
            </Container>
            {loadingState.userList ? (
                <Container
                    style={{
                        flex: 1,
                        minHeight: 'calc(100vh - 235px)',
                        maxHeight: 'calc(100vh - 235px)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress size={50} />
                </Container>
            ) : (
                <OrganizationUserTable
                    users={filteredUserList}
                    userListActivityMap={userListActivityMap}
                    onClickRemoveUserFromWorkspaces={
                        onClickRemoveUserFromWorkspaces
                    }
                    onClickAddUser={onClickAddUser}
                    onClickUserActivity={onClickUserActivity}
                    onMarkAsRegionalManager={onClickMarkAsRegionalManager}
                    onMarkAsPropertyManager={onClickMarkAsPropertyManager}
                />
            )}
            <Modal
                open={modalState.removeUserWorkspacesModalOpen}
                onClose={handleCloseRemoveUserWorkspaces}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide
                    direction="up"
                    in={modalState.removeUserWorkspacesModalOpen}
                >
                    <Paper
                        style={{
                            height: 'calc(100vh - 200px)',
                            width: '80%',
                            maxWidth: '1000px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <RemoveUserWorkspaces
                            userDetail={userDetail}
                            isLoading={loadingState.userDetail}
                            organization={organization}
                            onClose={handleCloseRemoveUserWorkspaces}
                            bulkArchiveUser={bulkArchiveUser}
                            setUserList={setUserList}
                        />
                    </Paper>
                </Slide>
            </Modal>
            <Modal
                open={modalState.addUserModalOpen}
                onClose={handleCloseAddUser}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={modalState.addUserModalOpen}>
                    <Paper
                        style={{
                            height: 'calc(100vh - 200px)',
                            width: '80%',
                            maxWidth: '1200px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <AddUserToWorkspaces
                            userDetail={userDetail}
                            isLoading={loadingState.userDetail}
                            organization={organization}
                            permissionRoleList={permissionRoleList}
                            companyList={companyList}
                            onClose={handleCloseAddUser}
                            bulkAddUserToWorkspaces={bulkAddUserToWorkspaces}
                        />
                    </Paper>
                </Slide>
            </Modal>
            <Modal
                open={modalState.userActivityModalOpen}
                onClose={handleCloseUserActivity}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={modalState.userActivityModalOpen}>
                    <Paper
                        style={{
                            height: 'calc(100vh - 200px)',
                            width: '80%',
                            maxWidth: '1200px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <UserActivitySummary
                            userDetail={userDetail}
                            isLoading={
                                loadingState.userDetail ||
                                loadingState.userDetailActivity
                            }
                            userDetailActivityMap={userDetailActivityMap}
                            onClose={handleCloseUserActivity}
                        />
                    </Paper>
                </Slide>
            </Modal>
            <Modal
                open={modalState.addNewUserModalOpen}
                onClose={handleCloseAddNewUser}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={modalState.addNewUserModalOpen}>
                    <Paper
                        style={{
                            height: 'calc(100vh - 200px)',
                            width: '80%',
                            maxWidth: '1200px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <AddNewUserToWorkspaces
                            isLoading={loadingState.userDetail}
                            organization={organization}
                            permissionRoleList={permissionRoleList}
                            companyList={companyList}
                            onClose={handleCloseAddNewUser}
                            bulkAddUserToWorkspaces={bulkAddUserToWorkspaces}
                        />
                    </Paper>
                </Slide>
            </Modal>
        </Container>
    )
}

export default OrganizationUsersRoot

export interface UserPageModalState {
    removeUserWorkspacesModalOpen: boolean
    addUserModalOpen: boolean
    userActivityModalOpen: boolean
    addNewUserModalOpen: boolean
}
